import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { useAppState } from '../../context/AppStateContext';
import AttendanceRegistry from '../../components/teacher/AttendanceRegistry';
import { BookOpenIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const GlobalAttendanceManager = () => {
    const { courses, uniqueStudentCount } = useAppState();
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const { get } = useApi();

    useEffect(() => {
        if (courses.length > 0 && !selectedCourseId) {
            setSelectedCourseId(courses[0].id);
        }
    }, [courses, selectedCourseId]);

    // Fetch detailed course data (including enrolled students) for the selected ID
    const { data: activeCourse, isLoading: courseLoading } = useQuery({
        queryKey: ['course-detail', selectedCourseId],
        queryFn: () => get(`/teachers/api/courses/${selectedCourseId}/`),
        enabled: !!selectedCourseId,
        staleTime: 5 * 60 * 1000,
    });

    const totalStudents = uniqueStudentCount || 0;

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            <main className="max-w-7xl mx-auto py-6 md:py-10 px-4 sm:px-6 lg:px-8 space-y-6 md:space-y-10">
                <header className="hidden md:block relative overflow-hidden bg-[#18216D] rounded-[2.5rem] p-12 text-white shadow-2xl shadow-indigo-900/20">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                    <div className="relative z-10">
                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">Registry Control</p>
                        <h1 className="text-4xl sm:text-6xl font-black tracking-tighter italic">Global Attendance</h1>
                        <p className="text-indigo-100/70 max-w-2xl font-medium text-lg mt-6">
                            Manage attendance across all your learning areas from one centralized portal.
                        </p>
                    </div>
                </header>

                {/* Mobile Header */}
                <div className="md:hidden">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-1 w-8 bg-[#FFC425] rounded-full"></div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Registry Control</p>
                    </div>
                    <h1 className="text-4xl font-black text-[#18216D] tracking-tighter mb-6">Global Attendance</h1>

                    <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1 bg-white p-3 rounded-2xl border border-slate-100 flex items-center justify-between shadow-sm">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Classes</div>
                            <div className="text-lg font-black text-[#18216D]">{courses.length}</div>
                        </div>
                        <div className="flex-1 bg-[#18216D] p-3 rounded-2xl border border-[#18216D] flex items-center justify-between shadow-lg shadow-indigo-900/20">
                            <div className="text-[10px] font-black uppercase tracking-widest text-[#FFC425]">Students</div>
                            <div className="text-lg font-black text-white">{totalStudents}</div>
                        </div>
                    </div>
                </div>

                <section className="bg-transparent md:bg-white md:rounded-3xl md:p-6 md:shadow-sm md:border md:border-slate-100">
                    <div className="hidden md:flex items-center gap-4 mb-6">
                        <div className="h-10 w-10 bg-indigo-50 text-[#18216D] rounded-xl flex items-center justify-center">
                            <BookOpenIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight">Select Learning Area</h2>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Toggle current class view</p>
                        </div>
                    </div>

                    <div className="relative">
                        <button
                            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                            className="w-full flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 font-black text-[#18216D] uppercase tracking-widest text-xs hover:bg-white hover:shadow-md transition-all md:hidden"
                        >
                            <span>{activeCourse ? `${activeCourse.name} (${activeCourse.code})` : 'Select Learning Area'}</span>
                            {isAccordionOpen ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                        </button>
                        <div className={`flex flex-wrap gap-3 mt-4 md:mt-0 ${isAccordionOpen ? 'block' : 'hidden md:flex'}`}>
                            {courses.map(course => (
                                <button
                                    key={course.id}
                                    onClick={() => {
                                        setSelectedCourseId(course.id);
                                        setIsAccordionOpen(false);
                                    }}
                                    className={`w-full md:w-auto px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border-2 text-left md:text-center ${selectedCourseId === course.id
                                        ? 'bg-[#18216D] text-white border-[#18216D] shadow-lg shadow-indigo-900/20 md:scale-105'
                                        : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}
                                >
                                    {course.name} ({course.code})
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                {courseLoading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#18216D]/10 border-t-[#18216D] mb-4"></div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Fetching Class Roster...</p>
                    </div>
                ) : activeCourse ? (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <AttendanceRegistry courseId={activeCourse.id} students={activeCourse.enrolled_students || []} />
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-100">
                        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs italic">No learning areas assigned.</p>
                    </div>
                )}
            </main>
        </div>
    );
};

export default GlobalAttendanceManager;
